(() => {
  "use strict";
  const drawer = document.querySelector("[data-cart-drawer]");
  if (!drawer) return;

  const root = window.Shopify?.routes?.root || "/";
  const itemsNode = drawer.querySelector("[data-cart-drawer-items]");
  const totalNode = drawer.querySelector("[data-cart-drawer-total]");
  const statusNode = drawer.querySelector("[data-cart-drawer-status]");
  const progressNode = drawer.querySelector("[data-free-shipping-progress]");
  const progressMessage = drawer.querySelector("[data-free-shipping-message]");
  const progressMeter = drawer.querySelector("[data-free-shipping-meter]");
  const progressFill = drawer.querySelector("[data-free-shipping-fill]");
  const progressHint = drawer.querySelector("[data-free-shipping-hint]");
  const shippingProgress = window.JazzisCartShippingProgress;
  let busy = false;
  let closing = false;

  const money = (minor, currency) => shippingProgress.formatMoney(
    minor, currency, document.body.dataset.locale || "en-CA"
  );

  function updateProgress(cart) {
    if (!shippingProgress || !progressNode || !progressMessage || !progressMeter || !progressFill) return;
    const currency = cart.currency || document.body.dataset.currency || "CAD";
    const subtotal = Number(cart.items_subtotal_price);
    if (!Number.isFinite(subtotal)) {
      progressNode.hidden = true;
      if (statusNode) statusNode.textContent = "Shipping is confirmed at checkout.";
      return;
    }
    const offer = shippingProgress.getOffer(document.body.dataset.country || "CA", progressNode.dataset);
    const threshold = shippingProgress.getThresholdMinor(
      offer,
      currency,
      document.body.dataset.shopCurrency || "CAD",
      window.Shopify?.currency?.rate,
      document.body.dataset.locale || "en-CA"
    );
    if (!Number.isFinite(threshold) || threshold <= 0) {
      progressNode.hidden = true;
      if (statusNode) statusNode.textContent = "Shipping is confirmed at checkout.";
      return;
    }
    const calculation = shippingProgress.calculate(subtotal, threshold);
    if (!calculation) {
      progressNode.hidden = true;
      if (statusNode) statusNode.textContent = "Shipping is confirmed at checkout.";
      return;
    }
    const { remaining, percent, unlocked } = calculation;
    progressNode.hidden = false;
    progressMessage.textContent = !unlocked
      ? `You're only ${money(remaining, currency)} away from FREE ${offer.region} Shipping!`
      : `You've unlocked FREE ${offer.region} Shipping!`;
    progressMeter.setAttribute("aria-valuenow", String(percent));
    progressMeter.setAttribute("aria-valuetext", !unlocked ? `${money(remaining, currency)} remaining` : `Free ${offer.region} shipping unlocked`);
    progressFill.style.width = `${percent}%`;
    if (progressHint) progressHint.textContent = `Free ${offer.region} shipping on orders of ${money(threshold, currency)} or more.`;
  }

  function buildLine(item, cartCurrency) {
    const line = document.createElement("li");
    line.className = "cart-drawer__line";
    line.dataset.cartDrawerLine = "";
    line.dataset.lineKey = item.key || String(item.id || "");

    if (item.image) {
      const image = document.createElement("img");
      image.className = "cart-drawer__image";
      image.src = item.image;
      image.alt = item.product_title || item.title || "Candy";
      image.width = 68;
      image.height = 68;
      image.loading = "lazy";
      line.append(image);
    } else {
      const image = document.createElement("span");
      image.className = "cart-drawer__image";
      image.setAttribute("aria-hidden", "true");
      line.append(image);
    }

    const main = document.createElement("div");
    main.className = "cart-drawer__line-main";
    const heading = document.createElement("div");
    heading.className = "cart-drawer__line-head";
    const title = document.createElement("span");
    title.className = "cart-drawer__line-title";
    title.textContent = item.product_title || item.title || "Candy";
    const price = document.createElement("span");
    price.className = "cart-drawer__line-price";
    price.textContent = money(item.final_line_price ?? item.line_price ?? 0, cartCurrency);
    heading.append(title, price);
    main.append(heading);

    if (item.variant_title && item.variant_title !== "Default Title") {
      const variant = document.createElement("span");
      variant.className = "cart-drawer__variant";
      variant.textContent = item.variant_title;
      main.append(variant);
    }
    for (const [name, value] of Object.entries(item.properties || {})) {
      if (name.startsWith("_") || value == null || value === "") continue;
      const property = document.createElement("p");
      property.className = "cart-drawer__property";
      property.textContent = `${name}: ${value}`;
      main.append(property);
    }

    const form = document.createElement("form");
    form.className = "cart-drawer__line-controls";
    form.dataset.cartDrawerQuantityForm = "";
    form.dataset.lineKey = line.dataset.lineKey;
    const label = document.createElement("label");
    label.append(document.createTextNode("Qty "));
    const input = document.createElement("input");
    input.type = "number";
    input.min = "1";
    input.max = "999";
    input.value = String(item.quantity || 1);
    input.dataset.cartDrawerQuantity = "";
    input.setAttribute("aria-label", `Quantity for ${item.product_title || item.title || "Candy"}`);
    label.append(input);
    const update = document.createElement("button");
    update.type = "submit";
    update.textContent = "Update";
    const remove = document.createElement("button");
    remove.type = "button";
    remove.dataset.cartDrawerRemove = "";
    remove.textContent = "Remove";
    form.append(label, update, remove);
    main.append(form);
    line.append(main);
    return line;
  }

  function renderCart(cart) {
    if (!itemsNode) return;
    updateProgress(cart);
    if (totalNode) totalNode.textContent = money(cart.items_subtotal_price ?? cart.total_price ?? 0, cart.currency);
    itemsNode.replaceChildren();
    if (!Array.isArray(cart.items) || cart.items.length === 0) {
      const empty = document.createElement("div");
      empty.className = "cart-drawer__empty";
      empty.textContent = "Your cart is empty. Add a sweet treat to get started.";
      itemsNode.append(empty);
      return;
    }
    const list = document.createElement("ul");
    list.className = "cart-drawer__list";
    cart.items.forEach(item => list.append(buildLine(item, cart.currency)));
    itemsNode.append(list);
  }

  async function fetchCart() {
    const response = await fetch(`${root}cart.js`, {
      cache: "no-store",
      headers: { Accept: "application/json" }
    });
    const cart = await response.json();
    if (!response.ok || cart.status >= 400) {
      throw new Error(cart.description || "Your cart could not be loaded. Please try again.");
    }
    renderCart(cart);
    document.dispatchEvent(new CustomEvent("jazzis:cart-changed", { detail: cart }));
  }

  async function changeLine(form, quantity, button) {
    if (busy) return;
    const lineKey = form.dataset.lineKey;
    if (!lineKey || !Number.isInteger(quantity) || quantity < 0 || quantity > 999) return;
    busy = true;
    const oldLabel = button.textContent;
    button.disabled = true;
    button.textContent = "Updating…";
    if (statusNode) statusNode.textContent = "";
    try {
      const response = await fetch(`${root}cart/change.js`, {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({ id: lineKey, quantity })
      });
      const cart = await response.json();
      if (!response.ok || cart.status >= 400) throw new Error(cart.description || "That cart update could not be applied.");
      renderCart(cart);
      document.dispatchEvent(new CustomEvent("jazzis:cart-changed", { detail: cart }));
      if (statusNode) statusNode.textContent = quantity === 0 ? "Item removed from your cart." : "Cart updated.";
    } catch (error) {
      if (statusNode) statusNode.textContent = error instanceof Error ? error.message : "The cart could not be updated.";
    } finally {
      busy = false;
      if (button.isConnected) {
        button.disabled = false;
        button.textContent = oldLabel;
      }
    }
  }

  function closeDrawer() {
    if (!drawer.open || closing) return;
    closing = true;
    drawer.classList.add("is-closing");
    const delay = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 220;
    window.setTimeout(() => {
      drawer.classList.remove("is-closing");
      drawer.close();
      closing = false;
    }, delay);
  }

  drawer.querySelectorAll("[data-cart-drawer-close]").forEach(button => {
    button.addEventListener("click", event => {
      if (button.tagName === "A") event.preventDefault();
      closeDrawer();
    });
  });
  drawer.addEventListener("click", event => {
    if (event.target === drawer) closeDrawer();
  });
  drawer.addEventListener("cancel", event => {
    event.preventDefault();
    closeDrawer();
  });
  drawer.addEventListener("close", () => document.querySelector("[data-testid='button-basket-jar']")?.focus());
  drawer.addEventListener("submit", event => {
    const form = event.target.closest("[data-cart-drawer-quantity-form]");
    if (!form) return;
    event.preventDefault();
    const quantity = Number(form.querySelector("[data-cart-drawer-quantity]")?.value);
    const button = form.querySelector("[type='submit']");
    if (button) changeLine(form, quantity, button);
  });
  drawer.addEventListener("click", event => {
    const remove = event.target.closest("[data-cart-drawer-remove]");
    if (!remove) return;
    const form = remove.closest("[data-cart-drawer-quantity-form]");
    if (form) changeLine(form, 0, remove);
  });
  document.addEventListener("jazzis:cart-drawer-open", async () => {
    if (!drawer.open) return;
    if (statusNode) statusNode.textContent = "";
    try {
      await fetchCart();
    } catch (error) {
      if (statusNode) statusNode.textContent = error instanceof Error ? error.message : "Your cart could not be loaded.";
    }
  });
  document.addEventListener("jazzis:cart-updated", event => {
    if (drawer.open && event.detail) renderCart(event.detail);
  });
  document.addEventListener("jazzis:cart-added", event => {
    if (!event.detail) return;
    renderCart(event.detail);
    if (drawer.open) return;
    if (typeof drawer.showModal !== "function") {
      window.location.assign(document.body.dataset.cartUrl || `${root}cart`);
      return;
    }
    drawer.showModal();
    document.dispatchEvent(new CustomEvent("jazzis:cart-drawer-open"));
  });
})();
